import { getApi } from 'api';
import { AxiosResponse } from 'axios';
import { IContractData, IContractToEdit, IContractsCountPerWeek } from 'modules/contracts/types';
import ContractFilters from 'types/contract/IContractFilters';
import { DimonaDetails } from 'types/contract/DimonaDetails';
import IConfirmContract from 'types/contract/IConfirmContract';
import IConfirmHours from 'types/contract/IConfirmHours';
import IContractRequest from 'types/contract/IContractRequest';
import IValidateHours from 'types/contract/IValidateHours';
import IValidateResponse from 'types/contract/IValidateResponse';
import IWageData from 'types/contract/IWageData';
import dayjs from 'utils/dayjs';
import { IMultipleEmployeeContractRequest, IPostContractResponse, IWorkingLocationData } from 'types/contract';
import { fromValidateResponseToValidateReadyForDisplay } from 'mappers/contracts-mapper';
import { IContractCard } from 'types/contract/IContractCard';
import { isNaN } from 'lodash';
import { PaginatedList } from 'types/PaginatedList';
import IBlobWithName from 'types/documents/IBlobWithName';

export const saveMultipleEmployeeContractRequest = async (props: {
  contract: IMultipleEmployeeContractRequest;
  companyId: number;
}): Promise<number> => {
  const { companyId, contract } = props;
  const response: AxiosResponse<number> = await getApi().post(`Contract/SaveMultipleEmployeeContract`, contract, {
    headers: { companyId },
  });
  return response.data;
};

export const pasteSingleContractRequest = async (props: {
  contractDateId: number;
  date: string;
  companyId: number;
}): Promise<number> => {
  const { contractDateId, date, companyId } = props;
  const response: AxiosResponse<number> = await getApi().post(
    `Contract/PasteSingleContract`,
    { contractDateId, date },
    { headers: { companyId } },
  );
  return response.data;
};

export const pasteMultipleContractsRequest = async (props: {
  contractIds: number[];
  /** Monday of every target week, 'YYYY-MM-DD'. */
  weekStartsToPasteTo: string[];
  companyId: number;
}): Promise<IPostContractResponse[]> => {
  const { contractIds, weekStartsToPasteTo, companyId } = props;
  const response: AxiosResponse<IPostContractResponse[]> = await getApi().post(
    `Contract/PasteMultipleContracts`,
    { contractIds, weekStarts: weekStartsToPasteTo },
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const saveContractSlotRequest = async (props: {
  contract: IContractRequest;
  companyId: number;
  quantity: number;
}): Promise<number> => {
  const { companyId, contract, quantity } = props;
  const response: AxiosResponse<number> = await getApi().post(
    `Contract/SaveContractSlot`,
    { contract, quantity },
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const validateMultipleEmployeeContractRequest = async (props: {
  contract: IMultipleEmployeeContractRequest;
  companyId: number;
}): Promise<IValidateResponse> => {
  const { companyId, contract } = props;
  const response: AxiosResponse<IValidateResponse> = await getApi().post(
    `Contract/ValidateMultipleEmployeeContract`,
    contract,
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const validateContractSlotHoursRequest = async (props: {
  contract: IContractRequest;
  companyId: number;
}): Promise<IValidateResponse> => {
  const { companyId, contract } = props;
  const response: AxiosResponse<IValidateResponse> = await getApi().post(
    `Contract/ValidateContractSlotHours`,
    contract,
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const getWageRequest = async (wageData: IWageData): Promise<number> => {
  const { companyId } = wageData;
  const response: AxiosResponse<number> = await getApi().post(`Contract/GetMinimumWage`, wageData, {
    headers: { companyId },
  });
  return response.data;
};

export const getWorkingLocationRequest = async (workingLocationData: IWorkingLocationData): Promise<string> => {
  const { companyId, costCenterId } = workingLocationData;
  const response: AxiosResponse<string> = await getApi().get(
    `Contract/GetWorkingLocation?costCenterId=${costCenterId}`,
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const getContractForDateRequest = async (props: {
  contractDateId: number;
  companyId: string;
}): Promise<IConfirmContract> => {
  const { contractDateId, companyId } = props;
  const response: AxiosResponse<IConfirmContract> = await getApi().get(
    `Contract/GetContractByDateId?contractDateId=${contractDateId.toString()}`,
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const reopenConfirmationRequest = async (props: { contractDateId: number; companyId: number }): Promise<any> => {
  const { contractDateId, companyId } = props;
  const response: AxiosResponse<any> = await getApi().post(
    `Contract/ReopenConfirmation/${contractDateId.toString()}`,
    null,
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const confirmContractRequest = async (props: {
  contractHours: IConfirmHours;
  companyId: string;
}): Promise<IConfirmContract> => {
  const { contractHours, companyId } = props;
  const response: AxiosResponse<IConfirmContract> = await getApi().post(`Contract/Confirm`, contractHours, {
    headers: { companyId },
  });
  return response.data;
};

export const validateHoursOnConfirmContractRequest = async (props: {
  validateHours: IValidateHours;
  companyId: string;
}): Promise<IValidateResponse> => {
  const { companyId, validateHours } = props;
  const response: AxiosResponse<IValidateResponse> = await getApi().post(`Contract/ValidateHours`, validateHours, {
    headers: { companyId },
  });
  return fromValidateResponseToValidateReadyForDisplay(response.data);
};

export const validateHoursOnEditContractRequest = async (props: {
  contract: IContractToEdit;
  companyId: number;
}): Promise<IValidateResponse> => {
  const { contract, companyId } = props;
  const response: AxiosResponse<IValidateResponse> = await getApi().post(
    `Contract/ValidateHoursOnEditContract`,
    contract,
    { headers: { companyId } },
  );
  return response.data;
};

export const validateHoursOnMultipleConfirmContractRequest = async (props: {
  contractsData: any;
  companyId: number;
}): Promise<IValidateResponse> => {
  const { companyId, contractsData } = props;
  const response: AxiosResponse<IValidateResponse> = await getApi().post(
    `/Contract/ValidateHoursInBulk`,
    contractsData,
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const contractsMultipleConfirmationRequest = async (props: {
  contractsData: any;
  companyId: number;
}): Promise<Record<string, string>> => {
  const { companyId, contractsData } = props;
  const response: AxiosResponse<Record<string, string>> = await getApi().post(`/Contract/BulkConfirm`, contractsData, {
    headers: { companyId },
  });
  return response.data ?? {};
};

export const getContractsRequest = async (props: {
  companyId: number;
  filter: ContractFilters;
}): Promise<PaginatedList<IContractToEdit>> => {
  const { companyId, filter } = props;
  let response: AxiosResponse<PaginatedList<IContractToEdit>>;

  if (Number.isNaN(filter.year) || Number.isNaN(filter.week)) {
    return response.data;
  }

  if (filter.date) {
    filter.date = dayjs(new Date(filter.date)).format('DD/MM/YYYY');
  }

  if (!Number.isNaN(companyId) && companyId !== 0) {
    response = await getApi().post(`Contract/GetContractsByWeek`, filter, {
      headers: { companyId, 'Content-Type': 'application/json' },
    });
  } else {
    response = await getApi().post(`Contract/GetContractsByWeek`, filter, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  return response.data;
};

export const getContractCardsRequest = async (props: {
  companyId: number;
  filter: ContractFilters;
}): Promise<IContractCard[]> => {
  const { companyId, filter } = props;
  let response: AxiosResponse<IContractCard[]>;

  if (Number.isNaN(filter.year) || Number.isNaN(filter.week)) {
    return response.data;
  }

  if (filter.date) {
    filter.date = dayjs(new Date(filter.date)).format('DD/MM/YYYY');
  }

  if (!Number.isNaN(companyId) && companyId !== 0) {
    response = await getApi().post(`Contract/GetCardsByWeek`, filter, {
      headers: { companyId, 'Content-Type': 'application/json' },
    });
  } else {
    response = await getApi().post(`Contract/GetCardsByWeek`, filter, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  return response.data;
};

export const getContractByIdRequest = async (props: {
  companyId: number;
  contractId: number;
}): Promise<IContractToEdit> => {
  const { companyId, contractId } = props;
  const response: AxiosResponse<IContractToEdit> = await getApi().get(`Contract?id=${contractId}`, {
    headers: { companyId },
  });
  return response.data;
};

export const getContractsRequestForListView = async (props: {
  companyId: number;
  filter: ContractFilters;
}): Promise<IContractData[]> => {
  const { companyId, filter } = props;
  let response: AxiosResponse<IContractData[]>;

  if (Number.isNaN(filter.year) || Number.isNaN(filter.week)) {
    return response.data;
  }

  if (filter.date) {
    filter.date = dayjs(new Date(filter.date)).format('DD/MM/YYYY');
  }

  if (!Number.isNaN(companyId) && companyId !== 0) {
    response = await getApi().post(`Contract/GetContractsByWeekListView`, filter, {
      headers: { companyId, 'Content-Type': 'application/json' },
    });
  } else {
    response = await getApi().post(`Contract/GetContractsByWeekListView`, filter, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  return response.data;
};

export const getContractsOfAdjacentWeeksRequest = async (props: {
  companyId: number;
  year: number;
  weekNumber: number;
  locationId: number | undefined;
}): Promise<IContractCard[]> => {
  const { companyId, year, weekNumber, locationId } = props;
  let requestUrl = `Contract/GetContractsOfAdjacentWeeks?year=${year}&week=${weekNumber}`;
  if (locationId && !isNaN(locationId) && locationId > 0) {
    requestUrl += `&locationId=${locationId}`;
  }
  const response: AxiosResponse<IContractCard[]> = await getApi().get(requestUrl, {
    headers: { companyId },
  });
  return response.data;
};

export const getCostEstimationRequest = async (props: {
  companyId: number;
  weekNumber: number;
  year: number;
}): Promise<IValidateResponse> => {
  const { companyId, weekNumber, year } = props;
  const response: AxiosResponse<IValidateResponse> = await getApi().get(
    `Contract/GetCostEstimation/${weekNumber}/${year}`,
    {
      headers: { companyId },
    },
  );
  return response.data;
};

export const editContractRequest = async (props: {
  companyId: number;
  contract: IContractToEdit;
}): Promise<IValidateResponse> => {
  const { companyId, contract } = props;
  const response: AxiosResponse<IValidateResponse> = await getApi().put(`Contract`, contract, {
    headers: { companyId, 'Content-Type': 'application/json' },
  });
  return response.data;
};

export const deleteContractsRequest = async (props: {
  companyId: number;
  contractsIds: number[];
  confirmedDeleteSigned: boolean;
}): Promise<any> => {
  const { contractsIds, companyId, confirmedDeleteSigned } = props;
  const contractDateIds = contractsIds;
  const response: AxiosResponse<IValidateResponse> = await getApi().delete(`Contract`, {
    headers: {
      companyId,
      'Content-Type': 'application/json',
    },
    data: { contractDateIds, confirmedDeleteSigned },
  });
  return response.data;
};

export const getContractsCountFromPreviousWeeksRequest = async (props: {
  companyId?: number;
  year: number;
  weekNumber: number;
  locationId: number;
}): Promise<IContractsCountPerWeek[]> => {
  const { companyId, year, weekNumber, locationId } = props;
  let response: AxiosResponse<IContractsCountPerWeek[]>;
  if (companyId) {
    response = await getApi().get(
      `Contract/GetContractsCount?year=${year}&week=${weekNumber}&locationId=${locationId}&pastWeeksCount=3&futureWeeksCount=0`,
      {
        headers: { companyId },
        data: locationId,
      },
    );
  } else {
    response = await getApi().get(
      `Contract/GetContractsCount?year=${year}&week=${weekNumber}&locationId=${locationId}&pastWeeksCount=3&futureWeeksCount=0`,
    );
  }
  return response.data;
};

const getFileNameFromContentDisposition = (contentDisposition?: string): string | null => {
  if (!contentDisposition) return null;
  const encodedMatch = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(contentDisposition);
  if (encodedMatch) {
    try {
      return decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return null;
    }
  }
  const plainMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(contentDisposition);
  return plainMatch ? plainMatch[1].trim() : null;
};

export const downloadContractsRequest = async (props: {
  companyId: number;
  contractsData: any;
}): Promise<IBlobWithName> => {
  const { contractsData, companyId } = props;
  const response: AxiosResponse<Blob> = await getApi().post(`Contract/ExportContracts`, contractsData, {
    headers: { companyId },
    responseType: 'blob',
  });
  return {
    name: getFileNameFromContentDisposition(response.headers['content-disposition']),
    blob: response.data,
  };
};

export const downloadContractErrorsRequest = async (props: { errors: IPostContractResponse[] }): Promise<any> => {
  const { errors } = props;
  const response: AxiosResponse<number> = await getApi().post(`Contract/DownloadContractErrors`, errors, {
    responseType: 'blob',
  });
  return response.data;
};

export const downloadArchivedContractsRequestForCompanies = async (props: {
  companyPcPairList: number[][];
  type: string; // type would be 'archived' but for sake of reusability we will define as string
  year: number;
  week: number;
}): Promise<any> => {
  const { companyPcPairList, type, year, week } = props;
  const CompanyPcPairs = companyPcPairList.map(([first, second]) => ({ Company: first, Pc: second }));
  const response: AxiosResponse<number> = await getApi().post(
    `Contract/ExportContracts`,
    { CompanyPcPairs, type, year, week },
    {
      // headers: { companyId },
      responseType: 'blob',
    },
  );
  return response.data;
};

export const getContractDimonaNumberRequest = async (props: {
  companyId: number;
  contractId: number;
}): Promise<DimonaDetails> => {
  const { companyId, contractId } = props;
  const response: AxiosResponse<DimonaDetails> = await getApi().get(`Contract/GetDimonaNumber/${contractId}`, {
    headers: { companyId },
  });
  return response.data;
};
